import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { signOut } from '../lib/auth';

/**
 * Gates a page behind authentication.
 *
 * Important: we wait for `profile` (not just `user`) before rendering the
 * children. The Firestore Security Rules use the profile doc as the gate for
 * ALL reads — if the children mount and start subscribing to /leads before the
 * profile is loaded, they'll all get permission-denied even when the profile
 * is being self-healed in the background.
 */
export default function ProtectedRoute({ children, requireAdmin = false }) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (!user)   return <Navigate to="/login" state={{ from: location }} replace />;

  // Auth user exists but no profile yet — most likely the self-heal failed
  // because rules deny even the bootstrap write. Show a clear recovery screen
  // instead of letting children render and hit a wall.
  if (!profile) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="card p-6 border-red-200 bg-red-50 text-red-900">
          <div className="font-semibold mb-1">Your profile couldn't be set up</div>
          <p className="text-sm mb-3">
            You're signed in as <span className="font-mono">{user.email}</span> but your account profile
            couldn't be created in the database. This usually means the Firestore Security Rules need to
            be (re)deployed.
          </p>
          <div className="text-xs">
            <strong>Fix:</strong> Open PowerShell in <span className="font-mono">C:\APP\b1s-crm</span> and run:
            <div className="font-mono bg-red-100 p-2 rounded mt-1">firebase deploy --only firestore:rules</div>
            Then refresh this page (Ctrl + F5) or sign out and back in.
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={() => window.location.reload()} className="btn-primary">Try again</button>
            <button onClick={() => signOut().then(() => window.location.assign('/login'))} className="btn-ghost border border-slate-200">Sign out</button>
          </div>
        </div>
      </div>
    );
  }

  if (profile.disabled) {
    return (
      <div className="p-8">
        <h2 className="text-lg font-semibold mb-2">Account pending</h2>
        <p className="text-slate-600">Your account exists but has not yet been enabled. Ask an admin to approve you.</p>
      </div>
    );
  }
  if (requireAdmin && profile.role !== 'admin') {
    return <div className="p-8 text-slate-600">Admins only.</div>;
  }
  return children;
}
